import React from 'react';
import styled, { css } from 'styled-components';

import {
    Anchor,
    AnchorProps,
    Image,
    ImageProps,
    JSEventUtils,
    Logger,
} from 'UI-Dash-Commons-UI';

import SectionUtils from '../utils/SectionUtils';
import { URL_TYPE_NEW_TAB, URL_TYPE_NEW_WINDOW } from '../../FrameCollection/redux/constants';
import MenuSection from "../../../types/MenuSection";

const TAG = 'LeftSideMenuSectionComponent';
const DEFAULT_LEVEL = 3;

type LeftSideMenuSectionComponentProps = {
    activeSectionSpec: MenuSection,
    isLeftSideMenuOpen: boolean,
    level: number,
    onSectionActivated: ( sectionSpec: MenuSection ) => void,
    onSectionActivatedIframeWindowLocation: ( currentSectionSpec: MenuSection, newSectionSpec: MenuSection ) => void,
    onUpdateOpenSectionId: ( id: string | null ) => void,
    openSectionId: string,
    sectionSpec: MenuSection
};

const LeftSideMenuSectionComponent: React.FunctionComponent<LeftSideMenuSectionComponentProps> = ( {
                                                                                                        level,
                                                                                                        isLeftSideMenuOpen,
                                                                                                        sectionSpec,
                                                                                                        activeSectionSpec,
                                                                                                        openSectionId,
                                                                                                        onUpdateOpenSectionId,
                                                                                                        onSectionActivated,
                                                                                                        onSectionActivatedIframeWindowLocation
                                                                                                    } ) =>
{
    const adjustedLevel = level > 1 ? level - ( sectionSpec.totalLevels - DEFAULT_LEVEL ) : level;
    const isSectionOpen: boolean = openSectionId === sectionSpec.id;
    const isSectionActive: boolean = SectionUtils.isActive( sectionSpec, activeSectionSpec );

    const sectionImage: string | undefined = SectionUtils.findImage( sectionSpec.id, isSectionActive );

    const handleHeaderClick = ( event ) =>
    {
        JSEventUtils.stopEventPropagation( event );

        Logger.debug( TAG, 'Section clicked: \'' + sectionSpec.id + '\'' );

        if ( level === 1 )
        {
            if ( isSectionOpen )
            {
                onUpdateOpenSectionId( null );
            }
            else
            {
                onUpdateOpenSectionId( sectionSpec.id );
            }
        }

        if ( sectionSpec.sectionUrlType )
        {
            // If user was pressing the control type while clicking, we should open
            // this section in the new tab. Otherwise we should open it in the same tab
            const isModifierKeyHeld: boolean = event.metaKey || event.ctrlKey;
            const isShiftKeyHeld: boolean = event.shiftKey;

            if ( isModifierKeyHeld )
            {
                sectionSpec.sectionUrlType = URL_TYPE_NEW_TAB;
            }
            else if ( isShiftKeyHeld )
            {
                sectionSpec.sectionUrlType = URL_TYPE_NEW_WINDOW;
            }

            const activeSectionSpecModule = activeSectionSpec.sectionUrl.slice( 1 ).split( '/' );
            const sectionSpecModule = sectionSpec.sectionUrl.slice( 1 ).split( '/' );

            if (
                activeSectionSpecModule.length > 1 &&
                sectionSpecModule.length > 1 &&
                activeSectionSpecModule[ 0 ] === sectionSpecModule[ 0 ] &&
                activeSectionSpecModule[ 1 ] !== sectionSpecModule[ 1 ] && // product wants us to force reload a page when we're alrdy on the page but the left nav is clicked
                sectionSpec.sectionUrlType !== URL_TYPE_NEW_TAB &&
                sectionSpec.sectionUrlType !== URL_TYPE_NEW_WINDOW &&
                !sectionSpec.isForcePush
            )
            {
                // reroute using react router
                onSectionActivatedIframeWindowLocation(
                    activeSectionSpec,
                    sectionSpec
                );
            }
            else
            {
                // force update via history
                onSectionActivated( sectionSpec );
            }
        }
    };

    // First add the title
    return (
        <SectionItem
            isActive={isSectionActive}
            isOpen={isSectionOpen}
            isLeftSideMenuOpen={isLeftSideMenuOpen}
            level={level}
        >
            <SectionHeader
                adjustedLevel={adjustedLevel}
                level={level}
                isActive={isSectionActive}
                onClick={handleHeaderClick}
            >
                <SectionImage
                    level={level}
                    alt={sectionSpec.displayName}
                    src={sectionImage}
                    height={30}
                    width={30}
                />

                < SectionTitle
                    adjustedLevel={adjustedLevel}
                    level={level}
                    isActive={isSectionActive}
                    isLeftSideMenuOpen={isLeftSideMenuOpen}
                    text={sectionSpec.displayName}
                    href={sectionSpec.externalUrl ? sectionSpec.externalUrl : '#'}
                />
            </SectionHeader>

            <SectionChildren
                level={level}
                adjustedLevel={adjustedLevel}
                isOpen={isSectionOpen}
                isLeftSideMenuOpen={isLeftSideMenuOpen}
            >
                {
                    ( sectionSpec.children && sectionSpec.children.length ) ? sectionSpec.children.map(
                        ( childMenuSpec ) => (
                            <LeftSideMenuSectionComponent
                                key={childMenuSpec.id}
                                level={level + 1}
                                isLeftSideMenuOpen={isLeftSideMenuOpen}
                                sectionSpec={childMenuSpec}
                                activeSectionSpec={activeSectionSpec}
                                openSectionId={openSectionId}
                                onUpdateOpenSectionId={onUpdateOpenSectionId}
                                onSectionActivated={onSectionActivated}
                                onSectionActivatedIframeWindowLocation={onSectionActivatedIframeWindowLocation}
                            />
                        )
                    ) : ''
                }
            </SectionChildren>
        </SectionItem>
    );
};

const SectionItem = styled<{ isActive: boolean, isLeftSideMenuOpen: boolean, isOpen: boolean, level: number }, 'li'>( 'li' )`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;

  padding-left: 0;
  padding-left: ${props => props.level === 1 && '22px'};

  overflow: hidden;

  list-style-type: none;

  ${props => props.level === 1 && ( props.isActive || ( props.isLeftSideMenuOpen && props.isOpen ) ) && CssSectionActive};

  :hover {
    background-color: ${props => props.level === 1 && '#F2F2F2'};
  }
`;

const SectionHeader = styled<{ adjustedLevel: number, isActive: boolean, level: number }, 'div'>( 'div' )`
  display: none;
  display: ${props => props.adjustedLevel > 0 && 'flex'};
  
  align-items: center;
  
  min-width: 200px;

  padding-top: ${props => props.adjustedLevel === 1 && '15px'};
  padding-bottom: ${props => props.level === 1 && '10px'};

  margin-top: ${props => props.adjustedLevel === 2 && '12px'};

  margin-bottom: ${props => props.adjustedLevel === 2 && '15px'};
  margin-bottom: ${props => props.adjustedLevel === 3 && '12px'};

  letter-spacing: ${props => props.adjustedLevel === 2 && '1px;'};
  
  cursor: default;
  cursor: ${props => props.level === 1 && 'pointer'};
`;

const SectionImage = styled<{ level?: number } & ImageProps>( ( { level = 0, ...rest } ) =>
    <Image {...rest} /> )`
  display: none;
  display: ${props => props.level === 1 && 'inline-block'};

  vertical-align: middle;

  padding-right: 5px;
`;

const SectionTitle = styled<{ adjustedLevel: number, isActive: boolean, isLeftSideMenuOpen: boolean, level: number } & AnchorProps>( ( { isActive, isLeftSideMenuOpen, adjustedLevel, ...rest } ) =>
    <Anchor {...rest} /> )`
  display: inline-block;

  vertical-align: middle;

  text-decoration: none;

  font-family: ${props => props.theme.fonts.regular};
  font-family: ${props => props.adjustedLevel === 1 && props.level !== 1 && props.theme.fonts.black};
  font-family: ${props => props.level === 1 && props.isActive && props.theme.fonts.black};
  font-family: ${props => props.adjustedLevel === 3 && props.isActive && props.theme.fonts.medium};
  
  font-size: 15px;
  font-size: ${props => props.adjustedLevel === 1 && '14px'};
  font-size: ${props => props.adjustedLevel === 2 && '9px'};
  font-size: ${props => props.adjustedLevel === 3 && '14px'};

  font-size: ${props => props.level === 1 && '15px'};

  text-transform: ${props => props.adjustedLevel === 2 && 'uppercase'};

  color: #62696F;
  color: ${props => props.adjustedLevel === 1 && props.theme.colors.black};

  color: ${props => props.adjustedLevel === 2 && '#808D98'};

  color: ${props => props.adjustedLevel === 3 && props.isActive && '#5EBDDE'};
  color: ${props => props.adjustedLevel === 3 && !props.isActive && props.theme.colors.black};
  
  cursor: default;
  cursor: ${props => ( props.level === 1 || props.adjustedLevel === 3 ) && 'pointer'};

  opacity: 1;
  opacity: ${props => !props.isLeftSideMenuOpen && '0'};
  transition: opacity 0.3s linear;
`;

const SectionChildren = styled<{ isLeftSideMenuOpen: boolean, isOpen: boolean, level: number, adjustedLevel: number }, 'ul'>( 'ul' )`
  overflow: hidden;
  overflow-y: ${props => props.level === 0 && 'auto'};

  margin: 0;
  padding: 0;
  padding-left: ${props => props.adjustedLevel === 2 && '12px'}; // align clickable elements
  padding-left: ${props => props.level === 1 && '43px'};
  padding-bottom: ${props => props.level === 1 && '10px'};

  list-style-type: none;
  
  ${props => props.level === 1 && ( ( props.isOpen && props.isLeftSideMenuOpen ) ? CssSectionVisible : CssSectionHidden )};
`;

const CssSectionVisible = css`
  max-height: 2500px;
  transition: max-height 0.6s ease-in-out;
`;

const CssSectionHidden = css`
  max-height: 0px;
  transition: max-height 0.4s cubic-bezier(0, 1, 0, 1);
`;

const CssSectionActive = css`
  background-color: ${props => props.theme.colors.navTertiaryHighlight};
`;

export default LeftSideMenuSectionComponent;
